import { useRef, useState } from 'react';
import { useFormik } from 'formik';
import useAsyncEffect from 'use-async-effect';
import AssistantRagStorageService from '@shared/services/AssistantRagStorageService';
import DocumentParsingService from '@shared/services/DocumentParsingService';
import { triggers } from '@shared/constants/audioZoneTriggers';
import {
  IAssistantRagVectorStorage,
  ICreateAssistantRagVectorStorage,
  IUpdateAssistantRagVectorStorage,
} from '@shared/interfaces/assistantRagStorage';
import { ACCEPTED_MIME_TYPES, SourceMode } from '../utils';
import {
  validationSchemaCreate,
  validationSchemaCreateDoc,
  validationSchemaUpdate,
} from '../validationSchemas';

interface UseRagStorageFormParams {
  assistantRagVectorStorage?: IAssistantRagVectorStorage | null;
  tag?: string;
  tags?: string[];
  title?: string;
  mapName?: string;
  onSave: (
    values: ICreateAssistantRagVectorStorage | IUpdateAssistantRagVectorStorage,
  ) => Promise<void>;
}

export const useRagStorageForm = ({
  assistantRagVectorStorage,
  tag,
  tags,
  title,
  mapName,
  onSave,
}: UseRagStorageFormParams) => {
  const [allTags, setAllTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [sourceMode, setSourceMode] = useState<SourceMode>('text');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [uploadPct, setUploadPct] = useState<number>(0);
  const [submitError, setSubmitError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEditMode = Boolean(assistantRagVectorStorage?.ragVectorLinkId);
  const isDocBased =
    isEditMode && Boolean(assistantRagVectorStorage?.parsedDocumentId);

  const getInitialTags = () => {
    if (assistantRagVectorStorage?.ragVectorLinkId) {
      return assistantRagVectorStorage.tags ?? [];
    }
    if (tags) {
      return tags;
    }
    if (tag) {
      return [tag];
    }
    return [];
  };

  const getValidationSchema = () => {
    if (isEditMode) {
      return validationSchemaUpdate;
    }
    return sourceMode === 'document'
      ? validationSchemaCreateDoc
      : validationSchemaCreate;
  };

  const onSubmit = async ({
    textData,
    tags: submitTags,
    title: titleValue,
  }: Pick<IAssistantRagVectorStorage, 'textData' | 'tags'> & {
    title: string;
  }) => {
    const transformedTags = submitTags.map(
      (t) => triggers.find((x) => x.title === t)?.value ?? t,
    );

    setIsSubmitting(true);
    setSubmitError('');
    setUploadPct(0);

    try {
      if (isEditMode) {
        await onSave({
          tags: transformedTags,
          textData,
          title: titleValue,
          mapName,
        } as IUpdateAssistantRagVectorStorage);
      } else if (sourceMode === 'document') {
        if (!selectedFile) {
          setFileError('Please select a file');
          return;
        }
        const { documentId, uploadUrl } =
          await DocumentParsingService.createDocument({
            name: selectedFile.name,
            contentType: selectedFile.type,
          });
        await DocumentParsingService.uploadFile(
          uploadUrl,
          selectedFile,
          setUploadPct,
        );
        await onSave({
          parsedDocumentId: documentId,
          tags: transformedTags,
          title: titleValue,
          mapName,
        } as ICreateAssistantRagVectorStorage);
      } else {
        await onSave({
          textData,
          tags: transformedTags,
          title: titleValue,
          mapName,
        } as ICreateAssistantRagVectorStorage);
      }
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.';
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      tags: getInitialTags(),
      textData: assistantRagVectorStorage?.textData ?? '',
      title: assistantRagVectorStorage?.title ?? title ?? '',
    },
    validationSchema: getValidationSchema(),
    onSubmit,
  });

  const handleSourceModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: SourceMode | null,
  ) => {
    if (!newMode) {
      return;
    }
    setSourceMode(newMode);
    setSelectedFile(null);
    setFileError('');
    setSubmitError('');
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setFileError('');
    setSubmitError('');

    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      setFileError('Only PDF and DOCX files are supported');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setSelectedFile(file);
  };

  const isCreateButtonDisabled = () => {
    if (isSubmitting) {
      return true;
    }
    if (!isEditMode && sourceMode === 'document') {
      return !selectedFile;
    }
    return !(formik.dirty && formik.isValid);
  };

  const handleFormSubmit = () => {
    if (!isEditMode && sourceMode === 'document') {
      void onSubmit(formik.values);
    } else {
      formik.handleSubmit();
    }
  };

  useAsyncEffect(async () => {
    setIsLoading(true);

    if (
      assistantRagVectorStorage?.ragVectorLinkId &&
      assistantRagVectorStorage.s3Url
    ) {
      const text =
        await AssistantRagStorageService.getAssistantRagStorageS3TextData(
          {},
          assistantRagVectorStorage.s3Url,
        );
      formik.setFieldValue('textData', text);
    }

    const fetchedTags =
      await AssistantRagStorageService.getAssistantRagStorageTags();

    if (fetchedTags.length) {
      setAllTags(
        fetchedTags.map((t) => triggers.find((x) => x.value === t)?.title ?? t),
      );
    }

    setIsLoading(false);
  }, []);

  return {
    formik,
    allTags,
    isLoading,
    isSubmitting,
    sourceMode,
    selectedFile,
    fileError,
    uploadPct,
    submitError,
    isEditMode,
    isDocBased,
    fileInputRef,
    handleSourceModeChange,
    handleFileChange,
    isCreateButtonDisabled,
    handleFormSubmit,
  };
};
